import React, { useEffect, useRef, useState } from 'react';
import RegistrationPlate from '../common/RegistrationPlate';
import { StatusDot } from './StatusMark';
import { DocumentMark, SourceMark } from './icons';
import { formatDateLocalized, formatHistoricMileage, formatRegNumber, formatSekAmount } from '../../lib/format.jsx';
import { VEHICLE_RECORD_DEMO as DEMO } from './vehicleRecordDemo';
import { buildVehicleDocumentPaper, DOCUMENT_KIND } from './vehicleDocuments';
import './VehicleDealRecord.css';

// The vehicle record (Klargo-Reference VehicleDetail.tsx): breadcrumb, identity, the "Nuläge" ledger,
// a sticky section index, then Handlingar, Besiktning, Försäkring and Fakta. It is drawn from the
// vehicle of the user's OPEN deal. Values that the deal's car record holds come from the database;
// the ones Klargo does not store yet come from vehicleRecordDemo.js (hard-coded, as in the mockup).

const SECTION = { documents: 'handlingar', inspection: 'besiktning', insurance: 'forsakring', facts: 'fakta' };
const READING_LINE = 160;
const DAY_MS = 24 * 60 * 60 * 1000;

const toDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

// Calendar-only ranking of the deadline, like the reference: it never reads the result.
export function inspectionDeadline(nextBy, now = new Date()) {
  const next = toDate(nextBy);
  if (!next) return null;
  const days = Math.round((startOfDay(next) - startOfDay(now)) / DAY_MS);
  if (days < 0) return { status: 'overdue', days };
  if (days === 0) return { status: 'due-today', days };
  return { status: 'upcoming', days };
}

const isTrue = (v) => String(v).toLowerCase() === 'true';

// Swedish register values shown in an English UI (same maps as the mockup's vehicleFacts helper).
// Unknown values pass through unchanged.
const FUEL_EN = {
  Bensin: 'Petrol', Diesel: 'Diesel', El: 'Electric', 'Bensin/mildhybrid': 'Petrol / mild hybrid',
  Hybrid: 'Hybrid', Elhybrid: 'Hybrid', Laddhybrid: 'Plug-in hybrid', Etanol: 'Ethanol', Gas: 'Gas',
};
const isEn = (language) => Boolean(language && language.startsWith('en'));
const localiseFuel = (value, language) =>
  !value || !isEn(language) ? value : value.split(',').map((part) => FUEL_EN[part.trim()] || part.trim()).join(', ');
const pick = (obj, language) => (language && language.startsWith('en') ? obj.en : obj.sv);

const ProofRow = ({ tone, kicker, value, detail }) => (
  <div className="vd-proof">
    <div className="vd-proof-kicker">
      {tone ? <StatusDot tone={tone} /> : null}
      <span className="ds-label">{kicker}</span>
    </div>
    <div className="vd-proof-value">{value}</div>
    {detail ? <div className="vd-proof-detail">{detail}</div> : null}
  </div>
);

const FactRow = ({ label, value, mono, isLast, labelledByGroup }) => (
  <div className={`vd-fact${isLast ? '' : ' vd-fact--rule'}`}>
    <div className={`vd-fact-label${labelledByGroup ? ' vd-sr-only' : ''}`}>{label}</div>
    <div className={`vd-fact-value${mono ? ' ds-mono vd-fact-value--mono' : ''}${labelledByGroup ? ' vd-fact-value--alone' : ''}`}>{value}</div>
  </div>
);

const DocumentEntry = ({ title, meta, openLabel, onOpen }) => (
  <div className="vd-doc" data-testid="document-entry">
    <button type="button" className="vd-doc-btn" onClick={onOpen} data-testid="document-open">
      <span className="vd-doc-mark"><DocumentMark width={18} height={21} /></span>
      <span className="vd-doc-text">
        <span className="vd-doc-title">{title}</span>
        <span className="ds-meta vd-doc-meta">{meta}</span>
        <span className="vd-doc-open">
          <span className="ds-underline">{openLabel}</span>
          <span aria-hidden="true">→</span>
        </span>
      </span>
    </button>
  </div>
);

const SourceLine = ({ children }) => (
  <span className="vd-source ds-meta">
    <SourceMark width={12} height={12} />
    <span>{children}</span>
  </span>
);

export default function VehicleDealRecord({ t, tp, language, deal, onViewDeal, onOpenContract, onOpenTransactions, onOpenDocument }) {
  const [active, setActive] = useState(SECTION.documents);
  const [identityVisible, setIdentityVisible] = useState(false);
  const heroRef = useRef(null);
  const d = (value) => formatDateLocalized(value, language);
  const dateOf = (value) => d(value instanceof Date ? value.toISOString() : value);
  const sek = (n) => formatSekAmount(n, language);
  const f = (key) => t(`vehicleDeal.fact.${key}`);

  // ---- identity (database) ----
  const reg = deal.carInfoRegistrationNo || '';
  const make = deal.carInfoVehicleDesignation || '';
  const model = deal.carInfoTradeName || '';
  const name = [make, model].filter(Boolean).join(' ') || reg;
  const rawType = deal.vahicleTypeOptionName || deal.vahicleTypeOptionVahicleTypeName || '';
  const typeKey = `vehicleType.${rawType.toLowerCase()}.long`;
  const typeLabel = rawType && tp(typeKey) !== typeKey ? tp(typeKey) : rawType;
  const meta = [deal.carInfoYearModel, typeLabel].filter(Boolean).join(' · ');
  const retrievedAt = deal.creationTime || deal.fetchedAt;
  const retrieved = `Bilvision · ${tp('ds.document.verb.retrieved')} ${dateOf(retrievedAt)}`;
  const sampleLabel = tp('ds.sample.label');

  // ---- inspection: database when present, hard-coded otherwise ----
  const lastInspected = toDate(deal.carInfoInspectionDate) || toDate(DEMO.inspection.fallbackLastInspected);
  const realNext = toDate(deal.carInfoInspectionDateEMPTY);
  const deadline = realNext ? inspectionDeadline(deal.carInfoInspectionDateEMPTY) : null;
  const nextBy = realNext || toDate(DEMO.inspection.fallbackDeadline);
  const usingFallbackDeadline = !realNext;
  // Only the registry dates are real; the result (and, without dates, the whole section) is an example.
  const inspectionReal = Boolean(deal.carInfoInspectionDate || realNext);
  const headline = (() => {
    if (usingFallbackDeadline) return { text: tp('ds.inspection.daysLeft').replace('{days}', String(DEMO.inspection.fallbackDaysLeft)), attention: false };
    if (deadline.status === 'overdue') return { text: tp('ds.inspection.overdue'), attention: true };
    if (deadline.status === 'due-today') return { text: tp('ds.inspection.dueToday'), attention: true };
    const key = deadline.days === 1 ? 'ds.inspection.dayLeft' : 'ds.inspection.daysLeft';
    return { text: tp(key).replace('{days}', String(deadline.days)), attention: false };
  })();
  const deadlineText = tp('ds.inspection.deadline').replace('{date}', dateOf(nextBy));
  const mileageReal = Number(deal.carInfoFeedingStall) > 0;
  const mileageKm = mileageReal ? Number(deal.carInfoFeedingStall) : DEMO.inspection.fallbackMileageKm;
  const timeline = (() => {
    if (usingFallbackDeadline) {
      return { position: DEMO.inspection.fallbackTodayPosition, todayDate: dateOf(DEMO.inspection.fallbackTodayLabelDate) };
    }
    const span = nextBy.getTime() - lastInspected.getTime();
    const raw = span > 0 ? (Date.now() - lastInspected.getTime()) / span : 1;
    return { position: Math.min(1, Math.max(0, raw)), todayDate: dateOf(new Date()) };
  })();
  const inspectionDocDate = deal.carInfoInspectionDate ? dateOf(lastInspected) : dateOf(DEMO.inspection.fallbackDocumentDate);

  // ---- owner (database): the seller is the registered owner until the transfer is registered ----
  const ownerName = deal.sellerName || deal.sellerUserFullName || '';

  // ---- insurance (hard-coded, shown as an example) ----
  const ins = DEMO.insurance;

  // ---- documents ----
  // The contract line always reads as the signed purchase contract; its date comes from the database
  // (the seller's signing time, else the buyer's, else when the contract was created).
  const contractDate = deal.sellerSignedAt || deal.buyerSignedAt || deal.creationTime || retrievedAt;
  const contractMeta = `${tp('ds.record.contract.signedBoth')} · ${dateOf(contractDate)}`;
  const vehicleDocs = [
    { id: 'policy', kind: DOCUMENT_KIND.policy, title: tp('ds.document.insurancePolicy'), meta: `${sampleLabel} · ${ins.provider}` },
    { id: 'inspection', kind: DOCUMENT_KIND.inspection, title: tp('ds.document.inspectionReport'), meta: `${inspectionReal ? 'Bilvision' : sampleLabel} · ${inspectionDocDate}` },
  ];
  const purchaseDocs = [
    { id: 'contract', title: tp('ds.document.purchaseContract'), meta: contractMeta, label: tp('ds.record.contract.view'), contract: true },
    { id: 'report', kind: DOCUMENT_KIND.report, title: tp('ds.document.vehicleReport'), meta: `Bilvision · ${dateOf(retrievedAt)}` },
  ];
  const openPaper = (kind) => {
    const paper = buildVehicleDocumentPaper(kind, {
      tp, language, dateOf, reg, name, lastInspected, nextBy, mileageKm, retrievedAt, inspectionReal, mileageReal,
      reference: `#${deal.id}`,
      facts: columns.flatMap((c) => c.rows).map((r) => ({ label: r.label, value: r.value })),
    });
    onOpenDocument(paper, `Klargo-${kind}-${reg}.pdf`);
  };
  const openDoc = (doc) => (doc.contract ? onOpenContract() : openPaper(doc.kind));
  const column = (docs) =>
    docs.map((doc, i) => (
      <React.Fragment key={doc.id}>
        {i > 0 ? <div aria-hidden="true" className="vd-doc-rule" /> : null}
        <DocumentEntry title={doc.title} meta={doc.meta} openLabel={doc.label || tp('ds.document.view')} onOpen={() => openDoc(doc)} />
      </React.Fragment>
    ));

  // ---- facts ----
  const gearbox = (() => {
    const g = (deal.carInfoWaxelbarge || '').trim();
    if (g.toUpperCase() === 'A') return f('gearAuto');
    if (g.toUpperCase() === 'M') return f('gearManual');
    return g;
  })();
  const driveValue = deal.carInfoFourWheelDrive === undefined || deal.carInfoFourWheelDrive === null || deal.carInfoFourWheelDrive === ''
    ? ''
    : isTrue(deal.carInfoFourWheelDrive) ? f('awd') : f('twoWd');
  const columns = [
    {
      key: 'base',
      title: tp('ds.facts.group.base'),
      rows: [
        { key: 'reg', label: f('reg'), value: formatRegNumber(reg), mono: true },
        { key: 'year', label: f('year'), value: deal.carInfoYearModel },
        { key: 'type', label: f('type'), value: typeLabel },
        { key: 'first', label: f('firstInTraffic'), value: toDate(deal.carInfoRegistrationDate) ? dateOf(deal.carInfoRegistrationDate) : '' },
      ],
    },
    {
      key: 'drive',
      title: tp('ds.facts.group.drive'),
      rows: [
        { key: 'fuel', label: f('fuel'), value: localiseFuel(deal.carInfoFuel, language) },
        { key: 'gear', label: f('gearbox'), value: gearbox },
        { key: 'awd', label: f('drive'), value: driveValue },
      ],
    },
  ]
    .map((c) => ({ ...c, rows: c.rows.filter((r) => r.value) }))
    .filter((c) => c.rows.length > 0)
    // A compound heading must not name subjects the record does not hold: when a group collapses to one
    // fact, that fact names the group and its row shows the value alone (as in the mockup).
    .map((c) => (c.rows.length === 1 ? { ...c, title: c.rows[0].label, labelledByHeading: true } : c));

  const sections = [
    { id: SECTION.documents, label: tp('ds.section.documents') },
    { id: SECTION.inspection, label: tp('ds.section.inspection') },
    { id: SECTION.insurance, label: tp('ds.section.insurance') },
    { id: SECTION.facts, label: tp('ds.section.facts') },
  ];

  // The index names the chapter that owns the reading position; the identity fades into it once the
  // hero has left the screen.
  useEffect(() => {
    const ids = Object.values(SECTION);
    const sync = () => {
      const present = ids.filter((id) => document.getElementById(id));
      let current = present[0] || ids[0];
      present.forEach((id) => {
        if (document.getElementById(id).getBoundingClientRect().top <= READING_LINE) current = id;
      });
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      if (maxScroll > 2 && window.scrollY >= maxScroll - 2 && present.length) current = present[present.length - 1];
      setActive(current);
      const hero = heroRef.current;
      setIdentityVisible(hero ? hero.getBoundingClientRect().bottom < 122 : false);
    };
    sync();
    window.addEventListener('scroll', sync, { passive: true });
    return () => window.removeEventListener('scroll', sync);
  }, []);

  return (
    <section className="vd" data-testid="vehicle-deal-record">
      <div className="vd-scene">
      <div className="vd-atmosphere" aria-hidden="true">
        <div className="vd-atmosphere-rail ds-auth-rail">
          <div className="vd-atmosphere-wash" />
          <div className="vd-atmosphere-sheet" />
        </div>
      </div>
      <div className="vd-masthead ds-auth-rail">
        <div className="vd-crumb">
          <span>{tp('nav.vehicles')}</span>
          <span aria-hidden="true">/</span>
          <span className="ds-mono">{reg}</span>
        </div>

        <div ref={heroRef} className="vd-hero">
          <div className="vd-identity">
            <RegistrationPlate value={reg} size="hero" />
            <h1 className="vd-title" data-testid="vehicle-title">{name}</h1>
          </div>
          {meta ? <div className="vd-meta">{meta}</div> : null}

          <div className="vd-state" data-testid="dossier-state">
            <h2 className="vd-state-title">{tp('ds.section.currentStatus')}</h2>
            <div className="vd-state-band">
              <div className="vd-state-grid">
                <ProofRow tone="wine" kicker={tp('ds.vehicle.saleInProgress.kicker')} value={tp('ds.vehicle.saleInProgress')} detail={tp('ds.vehicle.saleInProgress.note')} />
                <ProofRow tone={headline.attention ? 'amber' : 'green'} kicker={tp('ds.section.inspection')} value={headline.text} detail={deadlineText} />
                <ProofRow kicker={tp('ds.section.insurance')} value={`${sampleLabel} · ${ins.provider}`} />
                {ownerName ? <ProofRow kicker={tp('ds.owner.registered')} value={ownerName} /> : null}
              </div>
              <div className="vd-state-foot">
                <button type="button" onClick={onViewDeal} data-testid="vehicle-view-deal" className="ds-action-text">
                  <span className="ds-underline">{tp('ds.vehicle.viewActiveDeal')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>

      <div className="vd-index">
        <div className="vd-index-row ds-auth-rail">
          <nav aria-label={t('vehicleDeal.nav')} className="vd-index-nav">
            {sections.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                aria-current={active === s.id ? 'location' : undefined}
                onClick={() => setActive(s.id)}
                className={`vd-index-tab${active === s.id ? ' vd-index-tab--on' : ''}`}
              >
                {s.label}
              </a>
            ))}
          </nav>
          <span className={`vd-index-id${identityVisible ? ' vd-index-id--on' : ''}`} aria-hidden={!identityVisible}>
            <RegistrationPlate value={reg} size="micro" />
            <span className="vd-index-id-name">{name}</span>
          </span>
        </div>
      </div>

      <div className="vd-zone">
        <div className="vd-zone-inner ds-auth-rail">
          {/* --- Handlingar --- */}
          <section id={SECTION.documents} data-testid="dossier-documents" className="vd-chapter">
            <h2 className="ds-title-section vd-chapter-title">{tp('ds.section.documents')}</h2>
            <div className="vd-rule vd-rule--strong">
              <div className="vd-sheet vd-sheet--docs">
                <div className="vd-docs-grid">
                  <div>
                    <h3 className="ds-label vd-docs-heading">{tp('ds.document.group.vehicle')}</h3>
                    {column(vehicleDocs)}
                  </div>
                  <div>
                    <h3 className="ds-label vd-docs-heading">{tp('ds.document.group.purchase')}</h3>
                    {column(purchaseDocs)}
                  </div>
                </div>
                <div className="vd-docs-foot">
                  <button type="button" onClick={onOpenTransactions} data-testid="vehicle-view-transaction" className="ds-action-text vd-docs-related">
                    <span className="ds-underline">{tp('ds.vehicle.relatedTransaction')}</span>
                    <span aria-hidden="true" className="ds-mark-fwd">→</span>
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* --- Besiktning --- */}
          <section id={SECTION.inspection} data-testid="dossier-inspection" className="vd-chapter">
            <h2 className="ds-title-section vd-chapter-title">{tp('ds.section.inspection')}</h2>
            <div className="vd-rule vd-rule--medium">
              <div className="vd-sheet" data-testid="vehicle-inspection-object">
                <div className="vd-insp-top">
                  <div className="vd-insp-left">
                    <div className="vd-insp-result-row">
                      <span aria-hidden="true" className="vd-insp-ring" style={{ borderColor: DEMO.inspection.approved ? 'var(--ds-green)' : 'var(--ds-amber)' }}>
                        <svg width="13" height="11" viewBox="0 0 14 12" style={{ fill: 'none', stroke: 'var(--ds-green)', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' }}>
                          <polyline points="2,6.5 5.5,10 12,2.5" />
                        </svg>
                      </span>
                      <span className="ds-title-section vd-insp-result">{DEMO.inspection.approved ? tp('ds.inspection.approved') : tp('ds.inspection.rejected')}</span>
                    </div>
                    <div className="vd-insp-note">{tp('ds.inspection.inspectedOn').replace('{date}', dateOf(lastInspected))}</div>
                  </div>
                  <div className="vd-insp-next">
                    <div className={`vd-insp-headline${headline.attention ? ' vd-insp-headline--attention' : ''}`}>{headline.text}</div>
                    <div className="vd-insp-deadline">{deadlineText}</div>
                  </div>
                </div>

                <div className="vd-line" aria-hidden="true">
                  <div className="vd-line-track" />
                  <span className="vd-line-end vd-line-end--start" />
                  <span className="vd-line-end vd-line-end--finish" />
                  <div className="vd-line-today" style={{ left: `${timeline.position * 100}%` }}>
                    <span className="vd-line-today-label">{tp('ds.inspection.today')}</span>
                    <span className="vd-line-today-date">{timeline.todayDate}</span>
                    <span className="vd-line-today-dot" />
                  </div>
                </div>

                <div className="vd-insp-foot">
                  {mileageReal ? (
                    <p className="vd-insp-mileage">
                      {tp('ds.inspection.mileage')} <span>{formatHistoricMileage(mileageKm, language)}</span>
                    </p>
                  ) : <span />}
                  <button type="button" onClick={() => openPaper(DOCUMENT_KIND.inspection)} data-testid="vehicle-inspection-view-report" className="ds-action-text">
                    <span className="ds-underline">{tp('ds.inspection.viewReport')}</span>
                    <span aria-hidden="true" className="ds-mark-fwd">→</span>
                  </button>
                </div>
              </div>
            </div>
            <p className="ds-meta vd-sample-note" data-testid="inspection-sample-note">
              {tp(inspectionReal ? 'ds.sample.inspectionResult' : 'ds.sample.inspection')}
            </p>
            {inspectionReal ? <div className="vd-source-row"><SourceLine>{retrieved}</SourceLine></div> : null}
          </section>

          {/* --- Försäkring --- */}
          <section id={SECTION.insurance} data-testid="dossier-insurance" className="vd-chapter">
            <h2 className="ds-title-section vd-chapter-title">{tp('ds.section.insurance')}</h2>
            <div className="vd-rule vd-rule--medium">
              <div className="vd-sheet vd-sheet--insurance" data-testid="vehicle-insurance-policy">
                <div className="vd-ins-top">
                  <div>
                    <div className="vd-ins-provider">{ins.provider}</div>
                    <div className="vd-ins-coverage">{pick(ins.coverage, language)}</div>
                  </div>
                  <div className="vd-ins-price">
                    <div className="vd-ins-monthly">{sek(ins.monthlyPremiumSek)}{t('vehicleDeal.perMonth')}</div>
                    <div className="ds-meta">{tp('ds.insurance.annual').replace('{amount}', sek(ins.annualPremiumSek))}</div>
                  </div>
                </div>
                <div className="vd-ins-facts">
                  <div>
                    <div className="ds-label">{tp('ds.insurance.deductible')}</div>
                    <div className="vd-ins-fact-value">{sek(ins.deductibleSek)}</div>
                  </div>
                  <div>
                    <div className="ds-label">{tp('ds.insurance.validFrom')}</div>
                    <div className="vd-ins-fact-value">{dateOf(ins.startDate)}</div>
                  </div>
                </div>
                <p className="ds-meta vd-ins-policy">
                  {tp('ds.insurance.policyNumber')} <span className="ds-mono">{ins.maskedPolicyNumber}</span>
                </p>
                <div className="vd-ins-foot">
                  <p className="ds-meta vd-ins-broker">{tp('ds.insurance.brokeredBy').replace('{broker}', ins.broker)}</p>
                  <button type="button" onClick={() => openPaper(DOCUMENT_KIND.policy)} data-testid="vehicle-insurance-view-policy" className="ds-action-text vd-nowrap">
                    <span className="ds-underline">{tp('ds.insurance.viewPolicy')}</span>
                    <span aria-hidden="true" className="ds-mark-fwd">→</span>
                  </button>
                </div>
              </div>
            </div>
            <p className="ds-meta vd-sample-note" data-testid="insurance-sample-note">{tp('ds.sample.insurance')}</p>
          </section>

          {/* --- Fakta --- */}
          <section id={SECTION.facts} data-testid="dossier-facts" className="vd-chapter">
            <h2 className="ds-title-section vd-chapter-title">{tp('ds.section.facts')}</h2>
            <div className="vd-facts vd-rule vd-rule--medium">
              {columns.map((c) => (
                <div key={c.key} className="vd-facts-col">
                  <div className="vd-facts-heading" aria-hidden={c.labelledByHeading || undefined}>{c.title}</div>
                  {c.rows.map((r, i) => (
                    <FactRow key={r.key} label={r.label} value={r.value} mono={r.mono} isLast={i === c.rows.length - 1} labelledByGroup={c.labelledByHeading} />
                  ))}
                </div>
              ))}
            </div>
            <div className="vd-source-row"><SourceLine>{retrieved}</SourceLine></div>
          </section>
        </div>
      </div>
    </section>
  );
}
